/*
 * Wellness Popup (3-Step) — Web Component
 * Pattern: Custom Element + inline JSON config from <script type="application/json">.
 * Klaviyo Client API endpoint: https://a.klaviyo.com/client/subscriptions/
 */
(function () {
  if (window.customElements && window.customElements.get('wellness-popup')) return;

  var KLAVIYO_REVISION = '2026-04-15';
  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var SUBMIT_TIMEOUT_MS = 10000;

  var COOKIE = {
    submitted: 'e1_wp_submitted',
    declined: 'e1_wp_declined',
    shown: 'e1_wp_shown'
  };

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeCookie(name, value, days) {
    var exp = new Date(Date.now() + days * 86400 * 1000).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + exp + '; path=/; SameSite=Lax';
  }

  function escapeRegex(s) {
    return s.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
  }

  function pathMatches(actual, pattern) {
    var trimmed = pattern.trim();
    if (!trimmed) return false;
    var regex = '^' + escapeRegex(trimmed).replace(/\\\*/g, '.*') + '$';
    try {
      return new RegExp(regex).test(actual);
    } catch (e) {
      return false;
    }
  }

  function isValidEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());
  }

  class WellnessPopup extends HTMLElement {
    constructor() {
      super();
      this.state = { step: 0, interest: null, email: null, submitting: false };
      this.config = null;
      this.refs = {};
      this.isOpen = false;
      this.hasOpened = false;
      this.triggerCleanups = [];
      this.abortController = null;

      this._onKeydown = this._onKeydown.bind(this);
      this._onOverlayClick = this._onOverlayClick.bind(this);
      this._onEditorReload = this._onEditorReload.bind(this);
    }

    connectedCallback() {
      try {
        var configEl = document.getElementById(this.dataset.configId);
        if (!configEl) return;
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('[wellness-popup] invalid config JSON', e);
        return;
      }

      if (!this.config.enabled) return;

      var inDesignMode = window.Shopify && window.Shopify.designMode;

      if (inDesignMode && !this.config.editorPreviewEnabled) return;

      var k = this.config.klaviyo || {};
      var configReady = !!(k.publicKey && k.listId);

      if (!configReady && !inDesignMode) {
        console.warn('[wellness-popup] disabled: Klaviyo public key or list id missing');
        return;
      }

      if (!this.config.testMode && !inDesignMode) {
        if (this._hasFrequencyCookie()) return;
        if (!this._matchesPageTarget()) return;
      }

      this._cacheRefs();
      this._bindCommonEvents();

      if (inDesignMode) {
        this.open();
        var previewStep = parseInt(this.config.editorPreviewStep || '1', 10);
        if (previewStep >= 2 && previewStep <= 3) {
          this._goToStep(previewStep);
        }
      } else {
        this._setupTriggers();
      }

      document.addEventListener('shopify:section:unload', this._onEditorReload);
    }

    disconnectedCallback() {
      this._cleanupTriggers();
      document.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('shopify:section:unload', this._onEditorReload);
      if (this.abortController) {
        try { this.abortController.abort(); } catch (e) {}
      }
      if (this.isOpen) {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      }
    }

    _onEditorReload(e) {
      if (e.target && e.target.contains(this)) {
        this._cleanupTriggers();
        if (this.isOpen) {
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
        }
      }
    }

    _cacheRefs() {
      this.refs.dialog = this.querySelector('[data-wp-dialog]');
      this.refs.overlay = this.querySelector('[data-wp-overlay]');
      this.refs.close = this.querySelector('[data-wp-close]');
      this.refs.steps = Array.prototype.slice.call(this.querySelectorAll('[data-wp-step]'));
      this.refs.options = Array.prototype.slice.call(this.querySelectorAll('[data-wp-option-value]'));
      this.refs.decline = this.querySelector('[data-wp-decline]');
      this.refs.form = this.querySelector('[data-wp-form]');
      this.refs.email = this.querySelector('[data-wp-email]');
      this.refs.submit = this.querySelector('[data-wp-submit]');
      this.refs.error = this.querySelector('[data-wp-error]');
      this.refs.copy = this.querySelector('[data-wp-copy]');
      this.refs.copyFeedback = this.querySelector('[data-wp-copy-feedback]');
      this.refs.code = this.querySelector('[data-wp-code]');
      this.refs.cta = this.querySelector('[data-wp-cta]');
    }

    _bindCommonEvents() {
      var self = this;

      if (this.refs.close) {
        this.refs.close.addEventListener('click', function () { self.close('close'); });
      }
      if (this.refs.overlay) {
        this.refs.overlay.addEventListener('click', this._onOverlayClick);
      }
      this.refs.options.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var value = btn.getAttribute('data-wp-option-value');
          self._onSelectInterest(value);
        });
      });
      if (this.refs.decline) {
        this.refs.decline.addEventListener('click', function () { self._onDecline(); });
      }
      if (this.refs.form) {
        this.refs.form.addEventListener('submit', function (e) {
          e.preventDefault();
          self._onSubmitEmail();
        });
      }
      if (this.refs.copy) {
        this.refs.copy.addEventListener('click', function () { self._onCopyCode(); });
      }
      if (this.refs.cta) {
        this.refs.cta.addEventListener('click', function (e) {
          var href = self.refs.cta.getAttribute('href');
          if (!href || href === '#' || href === '') {
            e.preventDefault();
            self.close('submit');
          }
          // Cookie wurde bei erfolgreichem Submit bereits gesetzt
        });
      }
    }

    _onOverlayClick() {
      this.close('close');
    }

    _onKeydown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        this.close('close');
      }
    }

    /* ---------- Triggers ---------- */
    _setupTriggers() {
      var self = this;
      var t = this.config.triggers || {};

      if (t.timeEnabled) {
        var timer = setTimeout(function () { self._tryOpen('time'); }, Math.max(0, (t.timeSeconds || 0) * 1000));
        this.triggerCleanups.push(function () { clearTimeout(timer); });
      }

      if (t.scrollEnabled) {
        var scrollTicking = false;
        var threshold = Math.min(100, Math.max(10, t.scrollPercent || 50));
        var onScroll = function () {
          if (scrollTicking) return;
          scrollTicking = true;
          requestAnimationFrame(function () {
            scrollTicking = false;
            var doc = document.documentElement;
            var maxScroll = doc.scrollHeight - window.innerHeight;
            if (maxScroll <= 0) return;
            var pct = ((window.scrollY || window.pageYOffset) + window.innerHeight) / doc.scrollHeight * 100;
            if (pct >= threshold) self._tryOpen('scroll');
          });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        this.triggerCleanups.push(function () { window.removeEventListener('scroll', onScroll); });
      }

      if (t.exitIntentEnabled) {
        this._setupExitIntent(t.exitIntentMobileFallback);
      }
    }

    _setupExitIntent(mobileFallbackEnabled) {
      var self = this;
      var isTouch = 'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0;
      var isMobileViewport = window.innerWidth < 768;

      if (isTouch || isMobileViewport) {
        if (!mobileFallbackEnabled) return;
        var lastY = window.scrollY || window.pageYOffset;
        var inactivityTimer = null;
        var onScroll = function () {
          var currentY = window.scrollY || window.pageYOffset;
          var dy = currentY - lastY;
          lastY = currentY;
          if (dy < -40) {
            if (inactivityTimer) clearTimeout(inactivityTimer);
            inactivityTimer = setTimeout(function () { self._tryOpen('exit-intent-mobile'); }, 3000);
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        this.triggerCleanups.push(function () {
          window.removeEventListener('scroll', onScroll);
          if (inactivityTimer) clearTimeout(inactivityTimer);
        });
      } else {
        var onMouseLeave = function (e) {
          if (e.clientY <= 0 && (e.relatedTarget === null || e.relatedTarget === undefined)) {
            self._tryOpen('exit-intent-desktop');
          }
        };
        document.documentElement.addEventListener('mouseleave', onMouseLeave);
        this.triggerCleanups.push(function () {
          document.documentElement.removeEventListener('mouseleave', onMouseLeave);
        });
      }
    }

    _tryOpen(reason) {
      if (this.isOpen || this.hasOpened) return;
      this.hasOpened = true;
      this._cleanupTriggers();
      this.open();
    }

    _cleanupTriggers() {
      this.triggerCleanups.forEach(function (fn) {
        try { fn(); } catch (e) {}
      });
      this.triggerCleanups = [];
    }

    /* ---------- Open / Close ---------- */
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: this.refs.dialog }));
      this._goToStep(1);
      document.addEventListener('keydown', this._onKeydown);

      var self = this;
      requestAnimationFrame(function () {
        var first = self.querySelector('[data-wp-focus]');
        if (first) try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); }
      });
    }

    close(reason) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      document.removeEventListener('keydown', this._onKeydown);

      if (this.config && !this.config.testMode) {
        var freq = this.config.frequency || {};
        if (reason === 'submit') {
          // already set in onSubmitEmail
        } else if (reason === 'decline') {
          writeCookie(COOKIE.declined, '1', freq.declineDays || 90);
        } else {
          writeCookie(COOKIE.shown, '1', freq.closeDays || 7);
        }
      }
    }

    _goToStep(n) {
      this.state.step = n;
      this.refs.steps.forEach(function (el) {
        var stepNum = parseInt(el.getAttribute('data-wp-step'), 10);
        el.classList.toggle('is-active', stepNum === n);
      });
      if (this.refs.error) {
        this.refs.error.classList.remove('is-visible');
        this.refs.error.textContent = '';
      }
      if (n === 2 && this.refs.email) {
        var input = this.refs.email;
        requestAnimationFrame(function () {
          try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
        });
      }
    }

    /* ---------- Step interactions ---------- */
    _onSelectInterest(value) {
      this.state.interest = value;
      this._goToStep(2);
    }

    _onDecline() {
      this.close('decline');
    }

    _onSubmitEmail() {
      if (this.state.submitting) return;
      var email = (this.refs.email && this.refs.email.value || '').trim();

      if (!isValidEmail(email)) {
        this._showError(this.config.messages.invalidEmail);
        if (this.refs.email) this.refs.email.classList.add('is-error');
        return;
      }
      if (this.refs.email) this.refs.email.classList.remove('is-error');

      var k = this.config.klaviyo || {};
      if (!k.publicKey || !k.listId) {
        this._showError(this.config.messages.configMissing);
        console.error('[wellness-popup] Klaviyo public key or list id missing');
        return;
      }

      this.state.email = email;
      this.state.submitting = true;
      if (this.refs.submit) {
        this.refs.submit.disabled = true;
        this.refs.submit.classList.add('is-loading');
      }

      var self = this;
      this._submitToKlaviyo(email, this.state.interest)
        .then(function (result) {
          self.state.submitting = false;
          if (self.refs.submit) {
            self.refs.submit.disabled = false;
            self.refs.submit.classList.remove('is-loading');
          }
          if (result.ok) {
            // Cookie früh setzen, falls User Tab schließt
            if (!self.config.testMode) {
              var days = (self.config.frequency && self.config.frequency.submitDays) || 365;
              writeCookie(COOKIE.submitted, '1', days);
            }
            if (self.refs.code) self.refs.code.textContent = self.config.discountCode || '';
            self._goToStep(3);
          } else {
            var msg = self.config.messages.serverError;
            if (result.error === 'rate_limited') msg = self.config.messages.rateLimited;
            self._showError(msg);
          }
        })
        .catch(function (err) {
          console.error('[wellness-popup] submit error', err);
          self.state.submitting = false;
          if (self.refs.submit) {
            self.refs.submit.disabled = false;
            self.refs.submit.classList.remove('is-loading');
          }
          self._showError(self.config.messages.serverError);
        });
    }

    _onCopyCode() {
      var self = this;
      var code = (this.refs.code && this.refs.code.textContent || '').trim();
      if (!code) return;

      var feedback = function () {
        if (!self.refs.copyFeedback) return;
        self.refs.copyFeedback.textContent = self.config.messages.copySuccess;
        self.refs.copyFeedback.classList.add('is-visible');
        setTimeout(function () {
          if (self.refs.copyFeedback) self.refs.copyFeedback.classList.remove('is-visible');
        }, 1800);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(feedback).catch(function () {
          self._copyFallback(code, feedback);
        });
      } else {
        this._copyFallback(code, feedback);
      }
    }

    _copyFallback(text, onDone) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); onDone(); } catch (e) {}
      document.body.removeChild(ta);
    }

    /* ---------- Klaviyo ---------- */
    _submitToKlaviyo(email, interest, retryCount) {
      retryCount = retryCount || 0;
      var k = this.config.klaviyo;
      var url = KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(k.publicKey);

      var properties = {};
      if (interest) properties[k.propertyName || 'interest'] = interest;

      var body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Popup (3-Step)',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: properties,
                  subscriptions: {
                    email: { marketing: { consent: 'SUBSCRIBED' } }
                  }
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: k.listId } }
          }
        }
      };

      this.abortController = new AbortController();
      var timeoutId = setTimeout(function () {
        if (this.abortController) try { this.abortController.abort(); } catch (e) {}
      }.bind(this), SUBMIT_TIMEOUT_MS);

      var self = this;
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Revision': KLAVIYO_REVISION
        },
        body: JSON.stringify(body),
        signal: this.abortController.signal
      })
        .then(function (res) {
          clearTimeout(timeoutId);
          if (res.status === 202) return { ok: true };
          if (res.status === 429) {
            if (retryCount < 2) {
              return new Promise(function (resolve) {
                setTimeout(function () { resolve(self._submitToKlaviyo(email, interest, retryCount + 1)); }, 1500 * Math.pow(2, retryCount));
              });
            }
            return { ok: false, error: 'rate_limited' };
          }
          if (res.status >= 500) {
            console.error('[wellness-popup] Klaviyo server error', res.status);
            if (retryCount < 1) {
              return new Promise(function (resolve) {
                setTimeout(function () { resolve(self._submitToKlaviyo(email, interest, retryCount + 1)); }, 1000);
              });
            }
            return { ok: false, error: 'server' };
          }
          return res.json().then(function (json) {
            var detail = json && json.errors && json.errors[0] && json.errors[0].detail;
            console.error('[wellness-popup] Klaviyo error', res.status, detail || '(no detail)', json);
            return { ok: false, error: detail || 'client_error', status: res.status };
          }).catch(function () {
            console.error('[wellness-popup] Klaviyo response unparseable', res.status);
            return { ok: false, error: 'client_error', status: res.status };
          });
        })
        .catch(function (err) {
          clearTimeout(timeoutId);
          if (err && err.name === 'AbortError') return { ok: false, error: 'timeout' };
          if (retryCount < 1) {
            return new Promise(function (resolve) {
              setTimeout(function () { resolve(self._submitToKlaviyo(email, interest, retryCount + 1)); }, 1000);
            });
          }
          return { ok: false, error: 'network' };
        });
    }

    /* ---------- Cookies & Targeting ---------- */
    _hasFrequencyCookie() {
      return readCookie(COOKIE.submitted) || readCookie(COOKIE.declined) || readCookie(COOKIE.shown);
    }

    _matchesPageTarget() {
      var t = this.config.targeting || {};
      var path = window.location.pathname;

      if (t.excludeCartCheckout && (path.indexOf('/cart') === 0 || path.indexOf('/checkout') === 0)) {
        return false;
      }
      if (!t.mode || t.mode === 'all') return true;

      var rawPaths = String(t.pathsRaw || '').split(/\r?\n/).map(function (s) { return s.trim(); }).filter(Boolean);
      if (rawPaths.length === 0) return t.mode === 'blacklist';

      var anyMatch = rawPaths.some(function (p) { return pathMatches(path, p); });
      return t.mode === 'whitelist' ? anyMatch : !anyMatch;
    }

    /* ---------- UI helpers ---------- */
    _showError(msg) {
      if (!this.refs.error) return;
      this.refs.error.textContent = msg || '';
      this.refs.error.classList.add('is-visible');
    }
  }

  customElements.define('wellness-popup', WellnessPopup);
})();
